// Painter für den Kneipenausbau (S7): Verfall-Overlays und Deko-Elemente, die mit dem Ausbau sichtbar werden.
import { P } from './palette.ts';
import { reg, FONT_DISPLAY, FONT_HAND } from './painters.ts';
import { type Ctx, Rng, shade, rgba, vgrad, radial, glow, dabs, roundRect, poly, woodGrain, contactShadow, grain, text } from './paint.ts';

/** Schmutz/Unrat auf dem Boden: Kronkorken, Scherben, Staubflecken */
reg('dirt', {
  size: (p) => ({ w: p.w ?? 1800, h: p.h ?? 300 }),
  draw(ctx, p, rng) {
    const w = p.w ?? 1800, h = p.h ?? 300;
    for (let i = 0; i < 14; i++) radial(ctx, rng.next() * w, rng.next() * h, rng.range(40, 120), [[0, rgba(P.wood0, 0.35)], [1, rgba(P.wood0, 0)]]);
    for (let i = 0; i < 40; i++) {
      ctx.fillStyle = rng.pick([P.brass1, P.iron2, P.green1, P.paper]);
      ctx.globalAlpha = 0.8;
      const x = rng.next() * w, y = rng.next() * h;
      if (rng.chance(0.5)) {
        ctx.beginPath();
        ctx.ellipse(x, y, 6, 3, 0, 0, Math.PI * 2);
        ctx.fill();
      } else poly(ctx, [x, y, x + rng.range(4, 12), y + rng.range(-4, 4), x + rng.range(-2, 6), y + rng.range(3, 8)]), ctx.fill();
    }
    ctx.globalAlpha = 1;
  },
});

/** Wasserflecken/Schimmel an der Wand */
reg('stains', {
  size: (p) => ({ w: p.w ?? 2400, h: p.h ?? 450, ax: 0, ay: 0 }),
  draw(ctx, p, rng) {
    const w = p.w ?? 2400, h = p.h ?? 450;
    for (let i = 0; i < 18; i++) radial(ctx, rng.next() * w, rng.next() * h, rng.range(50, 170), [[0, rgba(P.plaster0, 0.45)], [0.6, rgba(P.green0, 0.18)], [1, rgba(P.plaster0, 0)]]);
    for (let i = 0; i < 10; i++) {
      let x = rng.next() * w, y = rng.next() * h * 0.6;
      ctx.strokeStyle = rgba(P.wood0, 0.5);
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(x, y);
      for (let s = 0; s < 8; s++) ctx.lineTo((x += rng.range(-14, 14)), (y += rng.range(8, 20)));
      ctx.stroke();
    }
  },
});

/** Glanz auf frisch geschliffenen Dielen */
reg('floorshine', {
  size: (p) => ({ w: p.w ?? 2400, h: p.h ?? 350, ax: 0, ay: 0 }),
  draw(ctx, p) {
    const w = p.w ?? 2400, h = p.h ?? 350;
    ctx.globalCompositeOperation = 'lighter';
    for (let i = 0; i < 6; i++) radial(ctx, (w * (i + 0.5)) / 6, h * 0.4, 260, [[0, rgba(P.amber2, 0.1)], [1, rgba(P.amber2, 0)]]);
  },
});

/** Riss in der Fensterscheibe + Pappe */
reg('crack', {
  size: () => ({ w: 300, h: 300, ax: 0, ay: 0 }),
  draw(ctx, p, rng) {
    ctx.strokeStyle = rgba(P.white, 0.7);
    ctx.lineWidth = 2;
    const cx = 210, cy = 90;
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 + rng.range(-0.2, 0.2);
      let x = cx, y = cy;
      ctx.beginPath();
      ctx.moveTo(x, y);
      for (let s = 0; s < 5; s++) ctx.lineTo((x += Math.cos(a) * 18 + rng.range(-6, 6)), (y += Math.sin(a) * 18 + rng.range(-6, 6)));
      ctx.stroke();
    }
    ctx.fillStyle = P.plaster2;
    ctx.save();
    ctx.translate(40, 170);
    ctx.rotate(-0.08);
    ctx.fillRect(0, 0, 120, 100);
    ctx.strokeStyle = P.plaster0;
    ctx.strokeRect(0, 0, 120, 100);
    ctx.fillStyle = rgba(P.paper, 0.7);
    ctx.fillRect(-6, 20, 132, 10);
    ctx.fillRect(-6, 70, 132, 10);
    ctx.restore();
  },
});

/** Zettel „Defekt“ */
reg('note', {
  size: () => ({ w: 120, h: 90 }),
  draw(ctx, p) {
    ctx.save();
    ctx.translate(60, 45);
    ctx.rotate(-0.1);
    ctx.fillStyle = P.paper;
    ctx.fillRect(-55, -40, 110, 80);
    ctx.fillStyle = P.red1;
    ctx.beginPath();
    ctx.arc(0, -38, 5, 0, Math.PI * 2);
    ctx.fill();
    text(ctx, p.text ?? 'Defekt!', 0, 2, `700 26px ${FONT_HAND}`, P.red1);
    ctx.restore();
  },
});

/** Lichterkette */
reg('garland', {
  size: (p) => ({ w: p.w ?? 1800, h: 140, ax: 0, ay: 0 }),
  draw(ctx, p, rng) {
    const w = p.w ?? 1800;
    ctx.strokeStyle = P.ink;
    ctx.lineWidth = 2;
    const sag = (x: number) => 30 + Math.sin((x / w) * Math.PI * 4) ** 2 * 60;
    ctx.beginPath();
    for (let x = 0; x <= w; x += 20) ctx.lineTo(x, sag(x));
    ctx.stroke();
    for (let x = 30; x < w; x += 60) {
      const col = rng.pick([P.amber2, P.red2, P.sea2, P.green2, P.amber3]);
      ctx.fillStyle = col;
      ctx.beginPath();
      ctx.ellipse(x, sag(x) + 10, 6, 9, 0, 0, Math.PI * 2);
      ctx.fill();
      glow(ctx, x, sag(x) + 10, 26, col, 0.5);
    }
  },
});

/** Fischernetz mit Schwimmern und Seestern an der Wand */
reg('fishnet', {
  size: () => ({ w: 520, h: 300, ax: 0, ay: 0 }),
  draw(ctx, p, rng) {
    ctx.strokeStyle = rgba(P.plaster2, 0.7);
    ctx.lineWidth = 1.5;
    for (let i = 0; i <= 14; i++) {
      ctx.beginPath();
      ctx.moveTo(i * 37, 0);
      ctx.quadraticCurveTo(i * 37 + 20, 150, i * 37 - 10, 280 - Math.abs(7 - i) * 12);
      ctx.stroke();
    }
    for (let j = 1; j < 9; j++) {
      ctx.beginPath();
      for (let i = 0; i <= 14; i++) ctx.lineTo(i * 37 + Math.sin(j + i) * 4, j * 32 + Math.sin(i * 0.8) * 10);
      ctx.stroke();
    }
    for (let i = 0; i < 6; i++) {
      ctx.fillStyle = P.amber1;
      ctx.beginPath();
      ctx.arc(rng.range(40, 480), rng.range(40, 240), 12, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.fillStyle = P.red2;
    ctx.save();
    ctx.translate(380, 160);
    ctx.beginPath();
    for (let k = 0; k < 10; k++) {
      const r = k % 2 ? 10 : 28, a = (k / 10) * Math.PI * 2;
      ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
    }
    ctx.fill();
    ctx.restore();
  },
});

/** Leine mit Signalflaggen quer durch den Raum */
reg('flagline', {
  size: (p) => ({ w: p.w ?? 1600, h: 160, ax: 0, ay: 0 }),
  draw(ctx, p, rng) {
    const w = p.w ?? 1600;
    ctx.strokeStyle = P.plaster2;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, 20);
    ctx.quadraticCurveTo(w / 2, 110, w, 20);
    ctx.stroke();
    const cols = [P.red1, P.amber2, P.sea1, P.white, P.night2, P.green1, P.red2];
    for (let x = 40; x < w - 40; x += 70) {
      const t = x / w, y = 20 + 4 * t * (1 - t) * 90;
      ctx.fillStyle = rng.pick(cols);
      ctx.fillRect(x - 18, y, 36, 40);
      ctx.fillStyle = rng.pick(cols);
      if (rng.chance(0.5)) ctx.fillRect(x - 18, y + 14, 36, 12);
      else ctx.fillRect(x - 6, y, 12, 40);
    }
  },
});

/** Fotowand mit gerahmten Bildern vergangener Quizabende */
reg('photowall', {
  size: (p) => ({ w: 360 * ((p.s as number) ?? 1), h: 260 * ((p.s as number) ?? 1) }),
  draw(ctx, p, rng) {
    ctx.scale((p.s as number) ?? 1, (p.s as number) ?? 1);
    const frames: [number, number, number, number][] = [[0, 0, 110, 80], [125, 10, 90, 120], [230, 0, 130, 90], [10, 100, 100, 130], [125, 145, 110, 90], [250, 110, 100, 120]];
    for (const [x, y, w, h] of frames) {
      ctx.fillStyle = rgba(P.night0, 0.4);
      ctx.fillRect(x + 4, y + 6, w, h);
      ctx.fillStyle = rng.pick([P.wood1, P.brass1, P.ink]);
      ctx.fillRect(x, y, w, h);
      vgrad(ctx, x + 6, y + 6, w - 12, h - 12, [[0, P.plaster2], [1, P.plaster0]]);
      // Gruppe Menschen als Silhouetten
      for (let i = 0; i < 4; i++) {
        ctx.fillStyle = rgba(P.wood0, 0.7);
        ctx.beginPath();
        ctx.arc(x + 14 + i * ((w - 28) / 3), y + h * 0.45, 6, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillRect(x + 8 + i * ((w - 28) / 3), y + h * 0.52, 12, h * 0.35);
      }
      grain(ctx, x, y, w, h, 0.2);
    }
  },
});

/** Teppich vor der Theke */
reg('carpet', {
  size: () => ({ w: 900, h: 160 }),
  draw(ctx, p, rng) {
    ctx.fillStyle = P.red0;
    poly(ctx, [120, 0, 780, 0, 900, 160, 0, 160]);
    ctx.fill();
    ctx.strokeStyle = P.brass1;
    ctx.lineWidth = 6;
    poly(ctx, [150, 12, 750, 12, 860, 148, 40, 148]);
    ctx.stroke();
    ctx.fillStyle = rgba(P.amber2, 0.25);
    for (let i = 0; i < 9; i++) {
      const x = 180 + i * 70;
      poly(ctx, [x, 80, x + 20, 60, x + 40, 80, x + 20, 100]);
      ctx.fill();
    }
    dabs(ctx, rng, 0, 0, 900, 160, [P.red1, P.wood0], { count: 200, alpha: [0.05, 0.15] });
  },
});

/** Vitrine mit Pokalen und Wimpeln */
reg('vitrine', {
  size: () => ({ w: 220, h: 380 }),
  draw(ctx, p, rng) {
    contactShadow(ctx, 110, 374, 200, 26, 0.55);
    woodGrain(ctx, rng, 0, 0, 220, 374, P.wood1, false);
    ctx.fillStyle = rgba(P.sea2, 0.25);
    ctx.fillRect(14, 14, 192, 300);
    for (const y of [110, 210, 314]) {
      ctx.fillStyle = P.wood2;
      ctx.fillRect(14, y, 192, 8);
    }
    const cup = (x: number, y: number, s: number, col: string) => {
      ctx.fillStyle = col;
      ctx.beginPath();
      ctx.moveTo(x - 16 * s, y - 50 * s);
      ctx.lineTo(x + 16 * s, y - 50 * s);
      ctx.quadraticCurveTo(x + 16 * s, y - 20 * s, x, y - 16 * s);
      ctx.quadraticCurveTo(x - 16 * s, y - 20 * s, x - 16 * s, y - 50 * s);
      ctx.fill();
      ctx.fillRect(x - 3 * s, y - 16 * s, 6 * s, 10 * s);
      ctx.fillRect(x - 12 * s, y - 6 * s, 24 * s, 6 * s);
      glow(ctx, x, y - 34 * s, 22 * s, P.amber3, 0.3);
    };
    cup(70, 110, 1.2, P.brass2);
    cup(150, 110, 0.9, P.iron2);
    cup(110, 210, 1, P.brass1);
    for (let i = 0; i < 3; i++) {
      ctx.fillStyle = [P.red1, P.sea1, P.green1][i];
      poly(ctx, [30 + i * 60, 240, 80 + i * 60, 250, 30 + i * 60, 262]);
      ctx.fill();
    }
    ctx.fillStyle = rgba(P.white, 0.12);
    poly(ctx, [14, 14, 80, 14, 14, 200]);
    ctx.fill();
    text(ctx, 'Siegerinnen & Sieger', 110, 345, `600 16px ${FONT_HAND}`, P.brass2);
  },
});

/** Bierdeckel (Sammelobjekt) */
reg('coaster', {
  size: () => ({ w: 44, h: 26 }),
  draw(ctx, p) {
    ctx.fillStyle = P.paper;
    ctx.beginPath();
    ctx.ellipse(22, 14, 20, 10, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = p.color ?? P.red1;
    ctx.beginPath();
    ctx.ellipse(22, 14, 12, 6, 0, 0, Math.PI * 2);
    ctx.fill();
    glow(ctx, 22, 14, 26, P.amber3, 0.25);
  },
});

/** neues, edles Wirtshausschild */
reg('signnew', {
  size: (p) => ({ w: p.w ?? 520, h: 130 }),
  draw(ctx, p, rng) {
    const w = p.w ?? 520;
    ctx.fillStyle = rgba(P.night0, 0.5);
    roundRect(ctx, 8, 14, w - 8, 112, 18);
    ctx.fill();
    ctx.save();
    roundRect(ctx, 0, 0, w - 8, 112, 18);
    ctx.clip();
    woodGrain(ctx, rng, 0, 0, w, 112, P.sea0, true);
    ctx.restore();
    ctx.strokeStyle = P.brass2;
    ctx.lineWidth = 4;
    roundRect(ctx, 8, 8, w - 24, 96, 12);
    ctx.stroke();
    text(ctx, p.text ?? 'Zum Rostigen Anker', (w - 8) / 2, 52, `800 42px ${FONT_DISPLAY}`, P.brass2);
    text(ctx, '★ seit 1922 ★', (w - 8) / 2, 88, `600 18px ${FONT_HAND}`, P.amber3);
    glow(ctx, w / 2, 56, w * 0.5, P.amber2, 0.15);
  },
});

/** Werkzeugkiste (Ausbau-Menü) */
reg('toolbox', {
  size: () => ({ w: 160, h: 110 }),
  draw(ctx, p, rng) {
    contactShadow(ctx, 80, 104, 150, 20, 0.5);
    ctx.fillStyle = P.red1;
    roundRect(ctx, 6, 36, 148, 66, 6);
    ctx.fill();
    ctx.fillStyle = shade(P.red1, -0.3);
    ctx.fillRect(6, 60, 148, 6);
    ctx.strokeStyle = P.iron2;
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.moveTo(50, 36);
    ctx.lineTo(50, 18);
    ctx.lineTo(110, 18);
    ctx.lineTo(110, 36);
    ctx.stroke();
    ctx.fillStyle = P.iron2;
    ctx.fillRect(120, 6, 10, 34);
    ctx.fillStyle = P.wood3;
    ctx.fillRect(28, 10, 8, 30);
    text(ctx, 'Ausbau', 80, 84, `700 18px ${FONT_HAND}`, P.paper);
    void rng;
  },
});

/** Pokal-Replik (Brakenhoffs Schocken-Wetteinsatz) */
reg('trophy', {
  size: () => ({ w: 110, h: 150 }),
  draw(ctx) {
    contactShadow(ctx, 55, 146, 40, 6);
    // Sockel
    ctx.fillStyle = P.wood0;
    roundRect(ctx, 25, 118, 60, 28, 4);
    ctx.fill();
    ctx.fillStyle = P.brass1;
    ctx.fillRect(38, 126, 34, 10);
    // Schaft
    vgrad(ctx, 48, 86, 14, 34, [[0, P.brass2], [1, P.brass0]]);
    // Becher
    ctx.fillStyle = P.brass1;
    ctx.beginPath();
    ctx.moveTo(18, 14);
    ctx.lineTo(92, 14);
    ctx.quadraticCurveTo(90, 78, 55, 88);
    ctx.quadraticCurveTo(20, 78, 18, 14);
    ctx.fill();
    ctx.fillStyle = rgba(P.white, 0.35);
    ctx.beginPath();
    ctx.ellipse(36, 42, 6, 22, -0.15, 0, Math.PI * 2);
    ctx.fill();
    // Henkel
    ctx.strokeStyle = P.brass0;
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.arc(14, 36, 14, Math.PI * 0.5, Math.PI * 1.5);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(96, 36, 14, -Math.PI * 0.5, Math.PI * 0.5);
    ctx.stroke();
    glow(ctx, 55, 40, 60, P.amber2, 0.25);
  },
});
